import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Mail, Ticket, LogOut, KeyRound, CheckCircle2, Rocket, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { adminAxios, clearAdminToken, setAdminToken, API } from '../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ['Essential Oils', 'Indian Attars', 'Spice Oils', 'Sandalwood', 'Home & Wellness'];
const unitOptions = ['ml', 'L', 'g', 'kg', 'pcs', 'drops'];

export const Admin = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [username, setUsername] = useState('');
  const [credForm, setCredForm] = useState({ current_password: '', new_username: '', new_password: '' });
  const [credSaving, setCredSaving] = useState(false);

  const emptyProduct = {
    name: '',
    category: 'Essential Oils',
    base_price: 0,
    description: '',
    image: '',
    variants: [{ size: '10', unit: 'ml', price: 0, stock: 0 }],
    weight_per_ml: 1.0,
    in_stock: true,
    featured: false
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await adminAxios.get('/admin/me');
        setUsername(me.data.username);
      } catch {
        return;
      }
      fetchProducts();
    })();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    // Back-fill variants: older entries may have size like "10ml" — split into size+unit
    const normalizeVariant = (v) => {
      if (v.unit) return { size: String(v.size ?? ''), unit: v.unit, price: v.price ?? 0, stock: v.stock ?? 0 };
      const m = String(v.size || '').match(/^([\d.]+)\s*([a-zA-Z]+)?$/);
      return {
        size: m ? m[1] : String(v.size ?? ''),
        unit: (m && m[2]) ? m[2] : 'ml',
        price: v.price ?? 0,
        stock: v.stock ?? 0,
      };
    };
    const productWithVariants = {
      ...product,
      variants: product.variants && product.variants.length > 0
        ? product.variants.map(normalizeVariant)
        : [{ size: '10', unit: 'ml', price: product.price || 0, stock: 50 }]
    };
    setEditingProduct(productWithVariants);
    setIsEditing(true);
  };

  const handleAdd = () => {
    setEditingProduct(emptyProduct);
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!editingProduct.name || !editingProduct.description || !editingProduct.image) {
        toast.error('Please fill in all required fields');
        return;
      }
      const productToSave = {
        ...editingProduct,
        base_price: editingProduct.base_price || (editingProduct.variants[0]?.price || 0)
      };
      if (productToSave.id) {
        await adminAxios.put(`/products/${productToSave.id}`, productToSave);
        toast.success('Product updated — changes are LIVE now');
      } else {
        await adminAxios.post('/products', productToSave);
        toast.success('Product created — now LIVE on the website');
      }
      await fetchProducts();
      setIsEditing(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await adminAxios.delete(`/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...editingProduct.variants];
    if (field === 'unit' || field === 'size') {
      newVariants[index] = { ...newVariants[index], [field]: value };
    } else if (field === 'stock') {
      newVariants[index] = { ...newVariants[index], [field]: parseInt(value) || 0 };
    } else {
      newVariants[index] = { ...newVariants[index], [field]: parseFloat(value) || 0 };
    }
    setEditingProduct({ ...editingProduct, variants: newVariants });
  };

  const addVariant = () => {
    const last = editingProduct.variants[editingProduct.variants.length - 1] || {};
    setEditingProduct({
      ...editingProduct,
      variants: [...editingProduct.variants, { size: '', unit: last.unit || 'ml', price: 0, stock: 0 }],
    });
  };

  const removeVariant = (index) => {
    if (editingProduct.variants.length <= 1) {
      toast.error('At least one variant is required');
      return;
    }
    const newVariants = editingProduct.variants.filter((_, i) => i !== index);
    setEditingProduct({ ...editingProduct, variants: newVariants });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'video/mp4', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only images (JPEG, PNG, WEBP) and videos (MP4, WEBM) are allowed');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await adminAxios.post('/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setEditingProduct({ ...editingProduct, image: imageUrl });
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    await new Promise(res => setTimeout(res, 1400));
    setDeploying(false);
    toast.success('All changes are LIVE on www.krishnasugandh.in', {
      description: 'Your edits are published instantly — no rebuild needed.',
      icon: <CheckCircle2 className="text-green-400" />,
      duration: 5000,
    });
  };

  const handleLogout = () => {
    clearAdminToken();
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const handleChangeCreds = async (e) => {
    e.preventDefault();
    if (!credForm.current_password) {
      toast.error('Enter your current password');
      return;
    }
    if (!credForm.new_username && !credForm.new_password) {
      toast.error('Provide a new username or new password');
      return;
    }
    setCredSaving(true);
    try {
      const payload = { current_password: credForm.current_password };
      if (credForm.new_username) payload.new_username = credForm.new_username;
      if (credForm.new_password) payload.new_password = credForm.new_password;
      const { data } = await adminAxios.post('/admin/change-credentials', payload);
      setAdminToken(data.access_token);
      setUsername(data.username);
      setCredForm({ current_password: '', new_username: '', new_password: '' });
      setShowCreds(false);
      toast.success('Credentials updated successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setCredSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen pt-32 flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground">
              Product <span className="italic">Management</span>
            </h1>
            {username && (
              <p className="text-xs text-muted-foreground uppercase tracking-widest mt-2">
                Signed in as <span className="text-primary">{username}</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/admin/orders"
              data-testid="nav-admin-orders"
              className="border border-primary/30 text-primary hover:bg-primary/10 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center space-x-2"
            >
              <Package size={18} strokeWidth={1.5} />
              <span>Orders</span>
            </Link>
            <Link
              to="/admin/coupons"
              data-testid="nav-admin-coupons"
              className="border border-primary/30 text-primary hover:bg-primary/10 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center space-x-2"
            >
              <Ticket size={18} strokeWidth={1.5} />
              <span>Coupons</span>
            </Link>
            <Link
              to="/admin/inquiries"
              className="border border-primary/30 text-primary hover:bg-primary/10 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center space-x-2"
            >
              <Mail size={18} strokeWidth={1.5} />
              <span>Inquiries</span>
            </Link>
            <button
              onClick={() => setShowCreds(!showCreds)}
              data-testid="change-creds-toggle"
              className="border border-white/20 text-foreground hover:border-primary hover:text-primary rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center space-x-2"
            >
              <KeyRound size={18} strokeWidth={1.5} />
              <span>Change Login</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="border border-destructive/40 text-destructive hover:bg-destructive/10 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center space-x-2"
            >
              <LogOut size={18} strokeWidth={1.5} />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Live / Deploy status banner */}
        <div className="border border-green-500/20 bg-green-500/5 px-6 py-4 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-green-400 flex-shrink-0 mt-0.5" size={20} strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-foreground">Your changes publish instantly</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Every product edit is saved to the live database and appears on the website immediately — no deployment needed.
                The button is here purely for your reassurance.
              </p>
            </div>
          </div>
          <button
            onClick={handleDeploy}
            disabled={deploying}
            data-testid="manual-deploy-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium transition-all duration-300 flex items-center gap-2 disabled:opacity-60"
          >
            <Rocket size={16} strokeWidth={1.5} />
            <span>{deploying ? 'Publishing...' : 'Publish / Deploy'}</span>
          </button>
        </div>

        {showCreds && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="border border-primary/20 p-6 mb-8"
          >
            <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><KeyRound size={20} /> Change Username / Password</h3>
            <form onSubmit={handleChangeCreds} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Current Password *</label>
                <input
                  type="password"
                  value={credForm.current_password}
                  onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })}
                  required
                  data-testid="current-password-input"
                  className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">New Username</label>
                <input
                  type="text"
                  value={credForm.new_username}
                  onChange={(e) => setCredForm({ ...credForm, new_username: e.target.value })}
                  placeholder="leave blank to keep"
                  data-testid="new-username-input"
                  className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">New Password</label>
                <input
                  type="password"
                  value={credForm.new_password}
                  onChange={(e) => setCredForm({ ...credForm, new_password: e.target.value })}
                  placeholder="leave blank to keep"
                  data-testid="new-password-input"
                  className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none"
                />
              </div>
              <div className="md:col-span-3 flex gap-3">
                <button type="submit" disabled={credSaving} data-testid="save-creds-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium disabled:opacity-50">
                  {credSaving ? 'Saving...' : 'Update Credentials'}
                </button>
                <button type="button" onClick={() => setShowCreds(false)} className="border border-white/20 rounded-full px-6 py-3 uppercase tracking-widest text-xs font-medium hover:border-primary transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        <div className="flex justify-end mb-8">
          <button
            onClick={handleAdd}
            data-testid="add-product-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 flex items-center space-x-2"
          >
            <Plus size={20} strokeWidth={1.5} />
            <span>Add Product</span>
          </button>
        </div>

        {isEditing ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 p-8 space-y-6 mb-12"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display text-3xl">{editingProduct.id ? 'Edit' : 'New'} Product</h2>
              <button onClick={() => setIsEditing(false)} className="text-muted-foreground hover:text-foreground">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">Product Name *</label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 text-lg transition-colors duration-300 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">Category *</label>
                <select
                  value={editingProduct.category}
                  onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })}
                  className="w-full bg-background border-b border-white/20 px-0 py-4 focus:border-primary focus:ring-0 text-lg transition-colors duration-300 focus:outline-none"
                >
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">Product Image *</label>

                {editingProduct.image && (
                  <div className="mb-4">
                    <img
                      src={editingProduct.image}
                      alt="Product preview"
                      className="w-32 h-32 object-cover border border-white/20"
                    />
                  </div>
                )}

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Or paste image URL"
                      value={editingProduct.image}
                      onChange={(e) => setEditingProduct({ ...editingProduct, image: e.target.value })}
                      className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 text-lg transition-colors duration-300 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="image-upload"
                      className={`cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 rounded px-6 py-3 uppercase tracking-widest text-sm font-medium transition-all duration-300 inline-block ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploading ? 'Uploading...' : 'Upload Image/Video'}
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/webm"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Upload image/video (max 10MB) or paste URL. Supports: JPEG, PNG, WEBP, MP4, WEBM
                </p>
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">Weight per ML (grams)</label>
                <input
                  type="number"
                  step="0.1"
                  value={editingProduct.weight_per_ml}
                  onChange={(e) => setEditingProduct({ ...editingProduct, weight_per_ml: parseFloat(e.target.value) })}
                  className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 text-lg transition-colors duration-300 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest font-medium block mb-3">Description *</label>
              <textarea
                value={editingProduct.description}
                onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                rows={4}
                className="w-full bg-transparent border border-white/20 px-4 py-4 focus:border-primary focus:ring-0 text-lg transition-colors duration-300 focus:outline-none resize-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-2xl">Size Variants & Pricing</h3>
                <button
                  type="button"
                  onClick={addVariant}
                  data-testid="add-variant-button"
                  className="text-xs uppercase tracking-widest px-4 py-2 border border-primary/40 text-primary hover:bg-primary/10 transition-colors flex items-center gap-2"
                >
                  <Plus size={14} strokeWidth={1.8} /> Add Variant
                </button>
              </div>
              <div className="space-y-3">
                {editingProduct.variants.map((variant, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 p-4 border border-white/5 items-end">
                    <div className="col-span-3">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Size / Qty</label>
                      <input
                        type="text"
                        value={variant.size}
                        onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                        placeholder="e.g. 100 or 0.5"
                        data-testid={`variant-size-${index}`}
                        className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none text-base"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Unit</label>
                      <select
                        value={variant.unit || 'ml'}
                        onChange={(e) => handleVariantChange(index, 'unit', e.target.value)}
                        data-testid={`variant-unit-${index}`}
                        className="w-full bg-background border-b border-white/20 py-2 focus:border-primary focus:outline-none text-base"
                      >
                        {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variant.price}
                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                        data-testid={`variant-price-${index}`}
                        className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none text-base"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Stock</label>
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                        data-testid={`variant-stock-${index}`}
                        className="w-full bg-transparent border-b border-white/20 py-2 focus:border-primary focus:outline-none text-base"
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeVariant(index)}
                        data-testid={`remove-variant-${index}`}
                        className="text-destructive hover:text-destructive/70 p-2"
                        title="Remove variant"
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Display example: "100 g", "250 ml", "1 kg", "5 pcs". Leave size as number only — unit is appended automatically.
              </p>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={editingProduct.in_stock}
                  onChange={(e) => setEditingProduct({ ...editingProduct, in_stock: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="text-sm uppercase tracking-widest">In Stock</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={editingProduct.featured}
                  onChange={(e) => setEditingProduct({ ...editingProduct, featured: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="text-sm uppercase tracking-widest">Featured</span>
              </label>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-product-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={20} strokeWidth={1.5} />
              <span>{saving ? 'Saving...' : 'Save Product'}</span>
            </button>
          </motion.div>
        ) : null}

        <div className="grid grid-cols-1 gap-6" data-testid="products-list">
          {products.map((product) => (
            <div key={product.id} className="border border-white/5 p-6 flex gap-6">
              <img src={product.image} alt={product.name} className="w-32 h-32 object-cover" />
              <div className="flex-1">
                <h3 className="font-display text-2xl mb-1">{product.name}</h3>
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">{product.category}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                {product.variants && product.variants.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {product.variants.map((v, idx) => (
                      <span key={idx} className="text-xs px-3 py-1 border border-primary/30 rounded-full">
                        {v.size}{v.unit ? (v.unit === 'pcs' ? ' pcs' : ` ${v.unit}`) : ''}: ₹{v.price}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleEdit(product)}
                  data-testid={`edit-product-${product.id}`}
                  className="text-primary hover:text-primary/80 p-2"
                >
                  <Edit size={20} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  data-testid={`delete-product-${product.id}`}
                  className="text-destructive hover:text-destructive/80 p-2"
                >
                  <Trash2 size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
